<script>
export default {
  title: 'Project 1a: Correlation Coefficient',
  starterId: null
}
</script>

<template>
  <section>

    <Readings :readings="[
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Chapter 2'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Chapter 3'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Chapter 4'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Sections 5.1 - 5.8, 5.12'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Sections 25.1 - 25.3'
      },
      {
        title: '<cite>Mastering Matlab (1st Edition)</cite> Sections 32.1 - 32.2'
      },
    ]"/>

    <article>
      <h2>Problem</h2>
      <p>Write a MATLAB M-file that reads two arrays of equal size from the user
        and determines their <dfn>correlation coefficient</dfn>. The correlation
        coefficient is a number between –1 and 1; coefficient values close to –1 or
        1 mean that there is a linear relation between corresponding data values in
        the two arrays. To find the correlation coefficient between values in
        arrays <var>xvalues</var> and <var>yvalues</var>, do the following:</p>
      <ul>
        <li>Convert the values to standard units. That is, for each value in
          <var>xvalues</var>, subtract the mean of the <var>xvalues</var> values and divide by the
          <var>xvalues</var> standard deviation, and for each value in <var>yvalues</var>, subtract the
          mean of the <var>yvalues</var> values and divide by the <var>yvalues</var> standard
          deviation.</li>
        <li>
          <p>The average of the products of corresponding standard unit scores
            gives the correlation coefficient.*</p>

          <p><sub>*Source: <cite>Statistics</cite> by Freedman, Pisani, and
            Purves (Norton, 1978). Some authors describe a difference computation
            for the correlation coefficient, the result of dividing the sum of the
            products of corresponding standard unit scores by <var>N–1</var>
            instead of by <var>N</var>, where <var>N</var> is the number of scores
            in each list. This obviously produces a different value.</sub></p>
        </li>
      </ul>

      <p>Here's an example:</p>

      <table class="table table-bordered text-center">
        <tr>
          <th>
            <var>xvalues</var> values<br>
            (mean = 4, SD = 2)
          </th>
          <th>
            <var>yvalues</var> values<br>
            (mean = 7, SD = 4)
          </th>
          <th>
            <var>xvalues</var> values in<br>
            standard units
          </th>
          <th>
            <var>yvalues</var> values in<br>
            standard units
          </th>
          <th>products</th>
        </tr>
        <tr><td>1</td><td> 5</td><td>–1.5</td><td>–0.5</td><td> 0.75</td></tr>
        <tr><td>3</td><td> 9</td><td>–0.5</td><td> 0.5</td><td>–0.25</td></tr>
        <tr><td>4</td><td> 7</td><td> 0.0</td><td> 0.0</td><td> 0.0 </td></tr>
        <tr><td>5</td><td> 1</td><td> 0.5</td><td>–1.5</td><td>–0.75</td></tr>
        <tr><td>7</td><td>13</td><td> 1.5</td><td> 1.5</td><td> 2.25</td></tr>
      </table>

      <p>The correlation coefficient is 0.40.</p>
    </article>

    <article>
      <h2>Miscellaneous Requirements</h2>
      <p>Every line in your M-file should be an assignment to a scalar or an
        array. The first two lines should call the input function; others should
        carry out the computation. You should assign the correlation coefficient to
        a variable named answer in the last statement of the M-file.</p>

      <p>Matlab provides <code>std</code> and <code>corrcoef</code> functions
        that implement somewhat different computations. Don’t use these.</p>

      <p>Test your M-file on the above values as well as on a pair of arrays that
        produces a correlation coefficient of –0.5. Be prepared to verify your
        computation to a tutor.</p>
    </article>

    <article>
      <h2>Checklist</h2>
      <ul>
        <li>
          A correctly working M-file, each of whose lines is an assignment statement as described,
          and whose last line is an assignment to the variable <var>answer</var>.
        </li>
        <li>
          Avoidance of the builtin <code>std</code> and <code>corrcoef</code> functions.
        </li>
        <li>
          A printed listing of your command window, displaying the specified test cases and results.
        </li>
        <li>
          A printed listing of your M-file, with each line accompanied by comments that describe its
          purpose and its inputs.
        </li>
      </ul></article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/matlab/CorrelationCoefficient.vue